import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Phone } from "lucide-react";
import { WHATSAPP_URL } from "../lib/api";
import SEO from "../components/SEO";

const SERVICES = {
  "corporate-lawyer": {
    badge: "Corporate Defence",
    title: "Corporate lawyer in Chandigarh arguing at the forum that matters.",
    intro:
      "From FIR stage to final arguments, Advocate Shubham Makkar represents clients in serious corporate matters before the Punjab & Haryana High Court, Sessions Court and Chief Judicial Magistrate, Chandigarh.",
    coverage: [
      "FIR quashing under Section 482 CrPC / 528 BNSS",
      "Writ Petitions (Corporate) before PHHC",
      "NDPS, POCSO and economic offences",
      "Trial representation and cross-examination",
      "Appeals and revision petitions",
    ],
    meta: {
      title: "Corporate Lawyer in Chandigarh | Advocate Shubham Makkar — PHHC",
      desc: "Corporate defence before Punjab &amp; Haryana High Court and Chandigarh courts. FIR quashing, writ petitions, trial defence, 498A, NDPS, POCSO.",
      kw: "corporate lawyer chandigarh, advocate chandigarh court, phhc lawyer, fir quashing lawyer chandigarh",
    },
  },
  "bail-lawyer": {
    badge: "Regular Bail",
    title: "Bail lawyer in Chandigarh — moved urgently, argued fully.",
    intro:
      "Post-arrest bail under Sections 437 / 439 CrPC (480 / 483 BNSS) demands both speed and substance. Advocate Shubham Makkar handles remand, interim bail and regular bail applications across Chandigarh Sessions Court and PHHC.",
    coverage: [
      "Remand hearings at Magistrate court",
      "Sessions Court bail under Section 439 CrPC",
      "Bail before PHHC in serious cases",
      "Default bail under Section 167(2) CrPC",
      "Interim bail in medical & family emergencies",
      "Conditions compliance and cancellation defence",
    ],
    meta: {
      title: "Bail Lawyer in Chandigarh | PHHC & Sessions Court — Advocate Shubham Makkar",
      desc: "Immediate bail applications in Chandigarh. Regular bail, interim bail and default bail before Sessions Court and Punjab &amp; Haryana High Court.",
      kw: "bail lawyer chandigarh, regular bail lawyer, phhc bail lawyer, sessions court bail chandigarh",
    },
  },
  "anticipatory-bail": {
    badge: "Pre-Arrest Protection",
    title: "Anticipatory bail in Chandigarh — drafted in hours, moved on priority.",
    intro:
      "When arrest appears imminent, every hour matters. Advocate Shubham Makkar drafts and moves anticipatory bail petitions under Section 438 CrPC / Section 482 BNSS before Sessions Court and PHHC.",
    coverage: [
      "Urgent drafting and filing within 24–48 hours",
      "Interim protection till final disposal",
      "Cross-FIR and counter-FIR strategy",
      "Surrender & regular-bail follow-through if denied",
      "Conditions negotiation (passport, cooperation, travel)",
      "Pre-arrest notices and anticipatory interventions",
    ],
    meta: {
      title: "Anticipatory Bail in Chandigarh | Section 438 CrPC Lawyer — PHHC",
      desc: "Anticipatory bail petitions drafted and moved urgently before PHHC and Sessions Court Chandigarh. Pre-arrest protection under Section 438 CrPC / 482 BNSS.",
      kw: "anticipatory bail chandigarh, section 438 crpc lawyer, phhc anticipatory bail lawyer",
    },
  },
};

export default function Services() {
  const { slug } = useParams();
  const svc = SERVICES[slug];
  if (!svc) return <Navigate to="/services/corporate-lawyer" replace />;

  return (
    <div data-testid={`service-page-${slug}`}>
      <SEO title={svc.meta.title} description={svc.meta.desc} keywords={svc.meta.kw} />

      <section className="relative pt-[72px] overflow-hidden bg-gradient-to-b from-[#EFF6FF] to-white">
        <div className="absolute inset-0 dot-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-16">
          <div className="inline-flex items-center gap-2 bg-white border border-blue-100 shadow-sm px-4 py-2 rounded-full mb-6">
            <span className="w-1.5 h-1.5 bg-[#1D4ED8] rounded-full"></span>
            <span className="text-xs tracking-wider text-[#1D4ED8] font-medium">
              {svc.badge} · Chandigarh
            </span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#0A2540] tracking-tight leading-[1.05] max-w-4xl">
            {svc.title}
          </h1>
          <p className="mt-8 text-lg text-slate-600 max-w-3xl leading-relaxed">{svc.intro}</p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/book" className="primary-btn" data-testid="service-book-btn">
              Book Consultation <ArrowRight size={16} />
            </Link>
            <a
              href={WHATSAPP_URL(`I need help with ${svc.badge}. Please call me.`)}
              target="_blank"
              rel="noopener noreferrer"
              className="ghost-btn"
              data-testid="service-whatsapp-btn"
            >
              <Phone size={16} /> WhatsApp Now
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="mb-3">
              <span className="eyebrow">Coverage</span>
            </div>
            <h2 className="font-serif text-4xl text-[#0A2540]">
              What we handle under this practice.
            </h2>
          </div>
          <ul className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {svc.coverage.map((item) => (
              <li key={item} className="flex items-start gap-3 card-elevated p-6">
                <div className="w-8 h-8 bg-[#DBEAFE] rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 size={16} className="text-[#1D4ED8]" />
                </div>
                <span className="text-slate-700 text-sm leading-relaxed pt-1">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-16 tint-band">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-serif text-3xl lg:text-4xl text-[#0A2540]">
            Unsure if your matter fits?
          </h2>
          <p className="mt-4 text-slate-600 text-lg">
            A 45-minute paid consultation gives you a clear strategy, fee schedule and next steps.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/book" className="primary-btn">
              Book for ₹1,500 <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
